import React from "react";
import type { DesignRisk, HirarcAssessment } from "../types/domain";

interface RiskMatrixProps {
  risks: DesignRisk[];
  hirarc: HirarcAssessment[];
}

export function RiskMatrix({ risks, hirarc }: RiskMatrixProps) {
  // A standard 5x5 risk matrix
  const matrix = Array(5).fill(0).map(() => Array(5).fill(0));

  risks.forEach(r => {
    if (r.residualLikelihood && r.residualSeverity) {
      // assuming 1-5 scale
      const l = Math.min(5, Math.max(1, r.residualLikelihood)) - 1;
      const s = Math.min(5, Math.max(1, r.residualSeverity)) - 1;
      matrix[4 - l][s] += 1; // 4-l so that likelihood 5 is at the top
    }
  });

  hirarc.forEach(h => {
    if (h.residualLikelihood && h.residualSeverity) {
      const l = Math.min(5, Math.max(1, h.residualLikelihood)) - 1;
      const s = Math.min(5, Math.max(1, h.residualSeverity)) - 1;
      matrix[4 - l][s] += 1;
    }
  });

  const getColor = (lIndex: number, sIndex: number) => {
    // Top right is red (high risk), bottom left is green (low risk)
    const likelihood = 5 - lIndex;
    const severity = sIndex + 1;
    const score = likelihood * severity;
    if (score >= 15) return "var(--red)"; // High
    if (score >= 8) return "var(--gold)"; // Medium
    return "var(--teal)"; // Low
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
      <div style={{ display: "flex", width: "100%", maxWidth: "500px" }}>
        <div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", textAlign: "center", padding: "1rem 0", fontWeight: "bold", color: "var(--muted)", fontSize: "0.8rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          Likelihood
        </div>
        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "2px", background: "var(--line)", border: "2px solid var(--line)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
          {matrix.map((row, lIndex) => 
            row.map((count, sIndex) => {
              const bg = getColor(lIndex, sIndex);
              return (
                <div 
                  key={`${lIndex}-${sIndex}`} 
                  style={{ 
                    aspectRatio: "1", 
                    background: count > 0 ? bg : "white", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    opacity: count > 0 ? 0.9 : 0.4,
                    transition: "var(--transition)",
                    cursor: count > 0 ? "pointer" : "default"
                  }}
                  title={`${count} active risks`}
                  onMouseEnter={(e) => {
                    if (count > 0) e.currentTarget.style.opacity = "1";
                  }}
                  onMouseLeave={(e) => {
                    if (count > 0) e.currentTarget.style.opacity = "0.9";
                  }}
                >
                  {count > 0 && (
                    <span style={{ 
                      background: "rgba(255,255,255,0.9)", 
                      color: bg, 
                      fontWeight: "bold", 
                      width: "24px", 
                      height: "24px", 
                      borderRadius: "50%", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center",
                      boxShadow: "var(--shadow-sm)",
                      fontSize: "12px"
                    }}>
                      {count}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
      <div style={{ textAlign: "center", padding: "1rem 0 0 0", fontWeight: "bold", color: "var(--muted)", fontSize: "0.8rem", letterSpacing: "0.1em", textTransform: "uppercase", marginLeft: "2rem" }}>
        Severity
      </div>
      <div style={{ display: "flex", gap: "1.5rem", marginTop: "1.5rem", marginLeft: "2rem", fontSize: "0.8rem", color: "var(--muted)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><div style={{ width: 12, height: 12, borderRadius: "50%", background: "var(--red)" }}></div>High Risk (≥15)</div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><div style={{ width: 12, height: 12, borderRadius: "50%", background: "var(--gold)" }}></div>Medium Risk (8-14)</div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><div style={{ width: 12, height: 12, borderRadius: "50%", background: "var(--teal)" }}></div>Low Risk (1-7)</div>
      </div>
    </div>
  );
}
